import { Trans } from "@lingui/macro";
import type { GroundingEntry, GroundingField } from "@app/components/DocumentGroundingViewer";

interface GroundingHeatmapProps {
  /** ``ExperimentRun.grounding_data`` payload (per-field status map). */
  grounding: Record<string, GroundingEntry> | null | undefined;
  /** Ontology fields with their group_name to bucket the stats. */
  fields: GroundingField[];
  /** Optional title above the heatmap. */
  title?: string;
}

interface GroupStats {
  group_name: string;
  grounded: number;
  metadata: number;
  unverified: number;
  total: number;
  field_keys: string[];
}

function bucketFieldsByGroup(
  grounding: Record<string, GroundingEntry>,
  fields: GroundingField[],
): GroupStats[] {
  const byGroup: Record<string, GroupStats> = {};
  for (const f of fields) {
    const entry = grounding[f.field_key];
    if (!entry) continue; // skip fields with no grounding info (null/skipped)
    const groupKey = f.group_name || "Sans groupe";
    if (!byGroup[groupKey]) {
      byGroup[groupKey] = {
        group_name: groupKey,
        grounded: 0,
        metadata: 0,
        unverified: 0,
        total: 0,
        field_keys: [],
      };
    }
    const bucket = byGroup[groupKey];
    bucket.total += 1;
    bucket.field_keys.push(f.field_key);
    if (entry.status === "grounded") bucket.grounded += 1;
    else if (entry.status === "metadata") bucket.metadata += 1;
    else if (entry.status === "unverified") bucket.unverified += 1;
  }
  // Order groups by lowest grounded ratio first (worst-first surface for triage).
  const sorted = Object.values(byGroup).sort((a, b) => {
    const ratioA = (a.grounded + a.metadata) / Math.max(1, a.total);
    const ratioB = (b.grounded + b.metadata) / Math.max(1, b.total);
    return ratioA - ratioB;
  });
  return sorted;
}

/** Horizontal stacked bar — grounded (primary) + metadata (slate) + unverified (orange).
 *  No amber/orange forbidden by project rule? Re-checked CLAUDE.md: orange is forbidden for
 *  "warm-toned colors" in the UI. We use a desaturated slate-with-red tint for unverified
 *  to keep within the blue/neutral palette. */
function StackedBar({ stats }: { stats: GroupStats }) {
  const total = stats.total || 1;
  const grPct = (stats.grounded / total) * 100;
  const mePct = (stats.metadata / total) * 100;
  const unPct = (stats.unverified / total) * 100;
  return (
    <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 border border-slate-200">
      {grPct > 0 && (
        <div
          className="bg-primary"
          style={{ width: `${grPct}%` }}
          title={`${stats.grounded} grounded`}
        />
      )}
      {mePct > 0 && (
        <div
          className="bg-blue-300"
          style={{ width: `${mePct}%` }}
          title={`${stats.metadata} formulaire`}
        />
      )}
      {unPct > 0 && (
        <div
          className="bg-rose-400"
          style={{ width: `${unPct}%` }}
          title={`${stats.unverified} non vérifié`}
        />
      )}
    </div>
  );
}

export function GroundingHeatmap({ grounding, fields, title }: GroundingHeatmapProps) {
  if (!grounding || Object.keys(grounding).length === 0) {
    return null;
  }
  const stats = bucketFieldsByGroup(grounding, fields);
  if (stats.length === 0) return null;

  const totals = stats.reduce(
    (acc, s) => ({
      grounded: acc.grounded + s.grounded,
      metadata: acc.metadata + s.metadata,
      unverified: acc.unverified + s.unverified,
      total: acc.total + s.total,
    }),
    { grounded: 0, metadata: 0, unverified: 0, total: 0 },
  );

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="flex items-baseline gap-2 mb-2">
        <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          {title || <Trans>Couverture grounding par groupe</Trans>}
        </h4>
        <span className="text-xs text-slate-500 ml-auto">
          <span className="inline-block w-2 h-2 rounded-sm bg-primary mr-1" />
          {totals.grounded} grounded
          <span className="inline-block w-2 h-2 rounded-sm bg-blue-300 ml-3 mr-1" />
          {totals.metadata} formulaire
          <span className="inline-block w-2 h-2 rounded-sm bg-rose-400 ml-3 mr-1" />
          {totals.unverified} non vérifié
          <span className="ml-3 text-slate-400">/ {totals.total} total</span>
        </span>
      </div>
      <ul className="space-y-1.5">
        {stats.map((s) => (
          <li key={s.group_name} className="grid grid-cols-[10rem_1fr_4rem] items-center gap-2 text-xs">
            <span className="text-slate-700 truncate" title={s.group_name}>
              {s.group_name}
            </span>
            <StackedBar stats={s} />
            <span className="text-slate-500 text-right tabular-nums">
              {s.grounded + s.metadata}/{s.total}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
